<script lang="ts" setup>
import {computed, ref} from 'vue';
import {captureException, captureMessage} from "@sentry/browser";
import QuizButton from '@/components/Quiz/QuizButton.vue'
import AddressList from '@/components/Quiz/AddressList.vue'
import AddressListText from '@/components/Quiz/AddressListText.vue'
import AddressForm from '@/components/Quiz/AddressForm.vue'
import {useQuizStore} from '@/stores/quizStore';
import QuizSecureTag from '@/components/Quiz/QuizSecureTag.vue'
import {appendData} from '@/services/quizService';
import {type IAddress} from '@/types/address';
import UiImage from "@/components/Ui/UiImage.vue";

const {
  addresses,
  isFetching,
  setIsFetching,
  goToNextStep,
  getStoredData,
  setAddresses,
  showAlert,
  newerSelectedVehicle
} = useQuizStore()

const MAX_ADDRESSES = 3;

const selectedAddresses = ref<IAddress[]>([...addresses.value]);

const isValid = computed(() => selectedAddresses.value.length)

function addAddress(address: IAddress) {
  if (selectedAddresses.value.length >= MAX_ADDRESSES) return
  selectedAddresses.value.push(address)

  if (selectedAddresses.value.length === MAX_ADDRESSES) {
    handleSubmit()
  }
}

function removeAddress(index: number) {
  selectedAddresses.value.splice(index, 1)
}

function handleSubmit() {
  if (!isValid.value) return

  const {contact, ...payload} = getStoredData()
  payload.addresses = [...selectedAddresses.value]

  setIsFetching(true)
  appendData(payload)
    .then(() => {
      setAddresses(selectedAddresses.value)
      goToNextStep()
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on appendData: AddressesSelection.vue");
        captureException(err);
      }
    })
    .finally(() => {
      setIsFetching(false)
    })
}
</script>

<template>
  <UiImage
    v-if="newerSelectedVehicle"
    :src="`https://cdn.imagin.studio/getImagem?make=${newerSelectedVehicle?.make || ''}&model=${newerSelectedVehicle?.model || ''}&modelYear=${newerSelectedVehicle?.year || ''}&customer=uk-johnsonlawgroup&countryCode=UK&fileType=jpg`"
    class="h-20 w-auto mx-auto"/>

  <img v-else src="@/assets/icons/location-icon.webp" alt="trophy" class="h-30 mx-auto py-2"/>

  <AddressListText :addresses="selectedAddresses" :max-addresses="MAX_ADDRESSES" />

  <AddressForm v-if="selectedAddresses.length < MAX_ADDRESSES" @add="addAddress"/>

  <AddressList :addresses="selectedAddresses" :max-addresses="MAX_ADDRESSES"
               @remove="removeAddress"/>

  <QuizButton label="Continue" :disabled="!isValid || isFetching" :isFetching="isFetching"
              @click="handleSubmit" id="address-selection-button" />

  <QuizSecureTag class="mt-6"/>
</template>
